import React, { useEffect, useState } from "react";
import {
  Select,
  Checkbox,
  Input,
  Button,
  Collapse,
  Form,
  InputNumber,
  DatePicker,
} from "antd";
import "./Filter.css";
import useCommonsService from "../../../services/useCommonsService";
import dayjs from "dayjs";

const { Search } = Input;
const { Panel } = Collapse;

const Filter = ({
  form,
  onFinish,
  responseFilters = null,
  selectedCityId,
  setSelectedCityId,
}) => {
  const [isTurkeySelected, setIsTurkeySelected] = useState(true);

  const {
    cities,
    isLoadingCities,
    useDistricts,
    useGetAllCountries,
    useGetAllJobTypes,
    useGetDurationTypes,
    useGetWagePeriods,
    useGetWageTypes,
    useGetAllJobOptions,
  } = useCommonsService();

  const { data: districts, isLoading: isLoadingDistricts } =
    useDistricts(selectedCityId);

  const { data: countries = [] } = useGetAllCountries();
  const { data: jobTypes = [] } = useGetAllJobTypes();
  const { data: durationTypes = [] } = useGetDurationTypes();
  const { data: wagePeriods = [] } = useGetWagePeriods();
  const { data: wageTypes = [] } = useGetWageTypes();
  const { data: jobOptions } = useGetAllJobOptions();

  const [activePanels, setActivePanels] = useState(["1"]);

  useEffect(() => {
    if (responseFilters) {
      const activeKeys = [];

      // Check each field and add corresponding panel key if it has a value
      if (
        responseFilters.countryIds ||
        responseFilters.cityIds ||
        responseFilters.districtIds
      ) {
        activeKeys.push("1");
      }
      if (responseFilters.durationTypes?.length) {
        activeKeys.push("2");
      }
      if (responseFilters.jobTypes?.length) {
        activeKeys.push("3");
      }
      if (responseFilters.wageTypes?.length) {
        activeKeys.push("10");
      }
      if (responseFilters.wagePeriods?.length) {
        activeKeys.push("11");
      }
      if (responseFilters.minWage || responseFilters.maxWage) {
        activeKeys.push("12");
      }
      if (responseFilters.startDate || responseFilters.endDate) {
        activeKeys.push("13");
      }
      if (responseFilters.isExperienceRequired) {
        activeKeys.push("14");
      }

      // Check job options
      if (responseFilters.optionIds && jobOptions) {
        jobOptions.forEach((section) => {
          const hasSelectedOptions = section.options.some((option) =>
            responseFilters.optionIds.includes(option.value)
          );
          if (hasSelectedOptions) {
            activeKeys.push(section.label);
          }
        });
      }

      setActivePanels(activeKeys);
    }
  }, [responseFilters, jobOptions]);

  useEffect(() => {
    if (responseFilters && jobOptions) {
      // Transform optionIds from flat array to sectioned format
      const transformedOptionIds = {};
      if (responseFilters.optionIds) {
        jobOptions.forEach((section) => {
          const sectionOptions = section.options || [];
          const selectedOptions = sectionOptions
            .filter((option) =>
              responseFilters.optionIds.includes(option.value)
            )
            .map((option) => option.value);

          if (selectedOptions.length > 0) {
            transformedOptionIds[section.label] = selectedOptions;
          }
        });
      }

      // Prepare form values
      const formValues = {
        ...responseFilters,
        // Handle date fields
        startDate: responseFilters.startDate
          ? dayjs(responseFilters.startDate)
          : null,
        endDate: responseFilters.endDate
          ? dayjs(responseFilters.endDate)
          : null,
        // Handle arrays that should be single values
        countryIds: responseFilters.countryIds?.[0] || null,
        cityIds: responseFilters.cityIds?.[0] || selectedCityId,
        districtIds: responseFilters.districtIds?.[0] || null,
        // Set transformed optionIds
        optionIds: transformedOptionIds,
      };

      // Update form
      form.setFieldsValue(formValues);

      // Update city selection state if needed
      if (formValues.cityIds) {
        setSelectedCityId(formValues.cityIds);
      }

      // Update Turkey selection state
      if (formValues.countryIds) {
        const selectedCountry = countries.find(
          (country) => country.value === formValues.countryIds
        );
        setIsTurkeySelected(selectedCountry?.label === "Türkiye");
      }
    }
  }, [responseFilters, jobOptions, countries, form]);

  const handleCityChange = (value) => {
    setSelectedCityId(value);
    form.setFieldValue("districtIds", undefined);
  };

  const handleCountryChange = (value) => {
    // Find the selected country from countries array
    const selectedCountry = countries.find(
      (country) => country.value === value
    );

    // Check if selected country is NOT Turkey
    const isForeignCountry =
      selectedCountry && selectedCountry.label !== "Türkiye";

    if (isForeignCountry) {
      // If foreign country is selected, disable city/district and clear their values
      setIsTurkeySelected(false);
      form.setFieldsValue({
        cityIds: undefined,
        districtIds: undefined,
      });
      setSelectedCityId(null);
    } else {
      // If Turkey or nothing is selected, enable city/district
      setIsTurkeySelected(true);
    }
  };

  const handlePanelChange = (keys) => {
    setActivePanels(keys);
  };

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <div className="filter-container">
        <Collapse
          activeKey={activePanels}
          onChange={handlePanelChange}
          ghost
          expandIconPosition="end">
          <Panel header="Ülke / Şehir / İlçe" key="1">
            <Form.Item style={{ marginBottom: 0 }} name="countryIds">
              <Select
                allowClear
                showSearch
                className="select-input"
                placeholder="Ülke seçin"
                options={countries}
                onChange={handleCountryChange}
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }} name="cityIds">
              <Select
                allowClear
                className="select-input"
                placeholder="Şehir seçin"
                value={selectedCityId}
                onChange={handleCityChange}
                options={cities || []}
                loading={isLoadingCities}
                disabled={!isTurkeySelected}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }} name="districtIds">
              <Select
                allowClear
                className="select-input"
                loading={isLoadingDistricts}
                options={districts || []}
                placeholder="İlçe"
                disabled={!isTurkeySelected || !selectedCityId}
              />
            </Form.Item>
          </Panel>

          <Panel header="İş Periyodu" key="2">
            <Form.Item name="durationTypes">
              <Checkbox.Group className="checkbox-group">
                {durationTypes.map((durationTypes) => (
                  <Checkbox
                    key={durationTypes.value}
                    value={durationTypes.value}>
                    {durationTypes.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Panel>

          <Panel header="İş Tipi" key="3">
            <Form.Item name="jobTypes">
              <Checkbox.Group className="checkbox-group">
                {jobTypes.map((jobTypes) => (
                  <Checkbox key={jobTypes.value} value={jobTypes.value}>
                    {jobTypes.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Panel>

          <Panel header="Maaş Tipi" key="10">
            <Form.Item name="wageTypes">
              <Checkbox.Group className="checkbox-group">
                {wageTypes.map((wageTypes) => (
                  <Checkbox key={wageTypes.value} value={wageTypes.value}>
                    {wageTypes.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Panel>

          <Panel header="Maaş Periyodu" key="11">
            <Form.Item name="wagePeriods">
              <Checkbox.Group className="checkbox-group">
                {wagePeriods.map((wagePeriods) => (
                  <Checkbox key={wagePeriods.value} value={wagePeriods.value}>
                    {wagePeriods.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Panel>
          <Panel header="Maaş Aralığı" key="12">
            <div style={{ display: "flex", gap: "12px" }}>
              <Form.Item name="minWage">
                <InputNumber
                  className="select-input"
                  placeholder="Min"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={1000}
                />
              </Form.Item>
              <Form.Item name="maxWage">
                <InputNumber
                  className="select-input"
                  placeholder="Max"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={1000}
                />
              </Form.Item>
            </div>
          </Panel>
          {jobOptions?.map((section, index) => (
            <Panel header={section.label} key={section.label}>
              <Form.Item
                name={["optionIds", section.label]}
                style={{ marginBottom: 0 }}>
                <Checkbox.Group className="checkbox-group">
                  {section.options.map((option) => (
                    <Checkbox key={option.value} value={option.value}>
                      {option.label}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </Panel>
          ))}

          <Panel header="Tarih Aralığı" key="13">
            <div style={{ display: "flex", gap: "12px" }}>
              <Form.Item name="startDate">
                <DatePicker
                  className="select-input"
                  placeholder="Başlangıç Tarihi"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
              <Form.Item name="endDate">
                <DatePicker
                  className="select-input"
                  placeholder="Bitiş Tarihi"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </div>
          </Panel>
          <Panel header="Tecrübe " key="14">
            <Form.Item
              name="isExperienceRequired"
              valuePropName="checked"
              initialValue={false}>
              <Checkbox>Tecrübe Aranan İlan</Checkbox>
            </Form.Item>
          </Panel>
        </Collapse>

        <div className="button-wrapper">
          <Button type="primary" className="apply-button" htmlType="submit">
            Uygula
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default Filter;
